import { useMemo, useState } from 'react'
import {useParams, Link} from 'react-router-dom'
import cn from 'clsx'
import {Space, Tooltip} from 'antd'

import styles from './ProjectDrawdown.module.scss'
import useStore from 'base/store'
import { PermissionType } from 'common/types'
import {ProfileRole} from 'modules/users'
import {
  DOCUMENTS_COLLECTION,
  IDocument,
  TDocumentsCollection,
} from 'modules/documents'
import {
  Box,
  Block,
  Modal,
  Text,
  Loader,
  Confirm,
  Empty,
  File,
  Button,
  Dropdown,
  IconButton,
  DropdownIcon,
  RotateArrowIcon,
  RotateArrowSixthIcon,
  BlackboardChartFifthIcon,
  PenEditSecondIcon,
  FilesIcon,
  IconTitle,
} from 'common/components'
import {useUploadFile, useGetForecast} from 'common/api'
import {useOpen} from 'common/hooks'
import {
  formatDate,
  formatMoney,
  isItemDrawdown,
  maybePluralize,
} from 'common/helpers'
import {ForecastItem} from 'common/types'
import {
  DrawdownAmountForm,
  DrawdownTag,
  DrawdownConfirm,
  useGetDrawdowns,
  useGetDrawdown,
  useUpdateDrawdown,
  useAcceptDrawdownDocuments,
  useApproveDrawdown,
  useDeclineDrawdown,
  useAttachDrawdownDocument,
  DrawdownItem,
  useRecallDrawdown,
} from '..'
import { DRAWDOWNSTATUS } from 'modules/request/constants.drawdown'
import PushbackModal from './PushBackModal'

interface ProjectDrawdownProps {
  drawdownUuid: string
}

interface DrawdownProps {
  projectUuid: string
  drawdownUuid: string
}

interface BorrowerDocumentsProps {
  documents: IDocument[]
}

interface ActionProps {
  projectUuid: string
  drawdown: DrawdownItem
  isRefetching: boolean
}

interface ForecastEditProps {
  projectUuid: string
  drawdown: DrawdownItem
  forecastDrawdowns: ForecastItem[]
  isRefetching: boolean
  isForecastRefetching: boolean
}

interface StatusActionsProps extends ActionProps {
  role: ProfileRole
  permissions: PermissionType[]
}

/**
 * drawdown详情
 * @param param0 drawdownUuid
 * @returns 
 */
export default function ProjectDrawdown({drawdownUuid}: ProjectDrawdownProps) {
  const params = useParams()
  const {data: drawdowns} = useGetDrawdowns(params.uuid)

  // console.table(drawdowns.map((x:any) => ({
  //   ...[
  //     'sortOrder',
  //     'title',
  //     'status',
  //     'requestedAmount',
  //     'approvedAmount'
  //   ].reduce((accu:any, key:string) => {
  //     accu[key] = x?.[key]
  //     return accu
  //   }, {} as any)
  // })))

  if (!drawdowns.length)
    return (
      <Block className={styles.root}>
        <div className={styles.wrapper}>
          <Empty
            icon={<RotateArrowIcon width="100%" height={40} />}
            title="drawdowns"
          />
        </div>
      </Block>
    )

  return <Drawdown projectUuid={params.uuid} drawdownUuid={drawdownUuid} />
}

const Drawdown = ({projectUuid, drawdownUuid}: DrawdownProps) => {
  const permissions = useStore((state) => state.permissions)
  const role = useStore((state) => state.role)
  const {data: drawdown, isRefetching} = useGetDrawdown(drawdownUuid)
  const {data: forecast, isRefetching: isForecastRefetching} = useGetForecast(
    projectUuid,
    !role.user
  )

  const isDocumentsEdit =
    false && Boolean(role.user && drawdown?.documentsUploadAllowed)

  const files: IDocument[] = drawdown
    ? [
        ...drawdown.buildingInspections,
        ...drawdown.cccAndTitles,
        ...drawdown.developmentSitePhotos,
        ...drawdown.drawdownNotices,
        ...drawdown.engineerReports,
        ...drawdown.geotechAndSurveyReports,
        ...drawdown.invoices,
        ...drawdown.preSales,
        ...drawdown.qsReports,
        ...drawdown.utilityCertificationAndCompliance,
      ]
    : []
  const filesCollections = Array.from(
    new Set(files.map((file) => file.collection))
  )

  const hasData = Boolean(drawdown && (role.user || (!role.user && forecast)))

  const forecastDrawdowns =
    !role.user && forecast
      ? forecast.filter((item) => isItemDrawdown(item))
      : []

  const forecastItem = hasData
    ? forecastDrawdowns.find((item) => item.drawdown?.uuid === drawdownUuid)
    : null

  const canEdit =
    drawdown &&
    (role.manager || role.coordinator) &&
    (drawdown.status === 'document-check')

  return (
    <div className={styles.root}>
      <Text
        uppercase
        spacing
        align="center"
        size="2xs"
        color="grey"
        weight="demiBold"
        pt={11}
        pb={12}
      >
        Details
      </Text>
      <Block className={styles.block}>
        {hasData ? (
          <>
            <DrawdownTag
              isBorrower={role.user}
              status={drawdown.status}
              updatedAt={drawdown.updatedAt}
            />
            <Text as="h3" size="2xl" weight="demiBold" mt={18} mb={8}>
              {drawdown.title}
            </Text>
            {drawdown.notes && <Text color="grey">{drawdown.notes}</Text>}
            {drawdown.reason && (
              <p className={styles['text-overflow']}>
                <Text color="redError" as='span'>Push back reason: </Text>
                <Tooltip title={drawdown.reason}>
                  <span>{drawdown.reason}</span>
                </Tooltip>
              </p>
            )}
            {!!files.length && (
              <Box mt={24} mb={12}>
                <IconTitle icon={<FilesIcon />} mb={6}>
                  Documents & photos
                </IconTitle>
                <Text color="grey" mb={12}>
                  {maybePluralize(files.length, 'file')} had been provided:{' '}
                  {filesCollections
                    .map(
                      (collection) =>
                        DOCUMENTS_COLLECTION[collection as TDocumentsCollection]
                          .label
                    )
                    .join(', ')
                    .toLocaleLowerCase()}
                </Text>
                {role.user ? (
                  <BorrowerDocuments documents={files} />
                ) : (
                  <Link
                    tabIndex={-1}
                    to={`/projects/${projectUuid}/documents/drawdowns#${drawdownUuid}`}
                  >
                    <Button variant="secondary">view documents</Button>
                  </Link>
                )}
              </Box>
            )}
            {isDocumentsEdit && (
              <DocumentAttach
                projectUuid={projectUuid}
                drawdownUuid={drawdownUuid}
              />
            )}
            {!role.user && (
              <Space className={styles.stat}>
                <BlackboardChartFifthIcon />
                <div>
                  <Space size={0}>
                    <Text size="2xl" weight="demiBold" color="turquoizeBright">
                      {forecastItem
                        ? `${formatMoney(forecastItem.value.amount, {
                            cents: true,
                          })} nzd`
                        : 'without reference'}
                    </Text>
                    {canEdit && (
                      <ForecastEdit
                        projectUuid={projectUuid}
                        drawdown={drawdown}
                        forecastDrawdowns={forecastDrawdowns}
                        isRefetching={isRefetching}
                        isForecastRefetching={isForecastRefetching}
                      />
                    )}
                  </Space>
                  <Text color="grey" weight="medium" size="xs">
                    {forecastItem
                      ? `Estimated amount, ${formatDate(forecastItem.date)}`
                      : 'No forecast for this drawdown'}
                  </Text>
                </div>
              </Space>
            )}
            <Space
              className={cn(styles.stat, role.user && styles.stat_borrower)}
            >
              <RotateArrowSixthIcon />
              <div>
                <Space size={0}>
                  <Text size="2xl" weight="demiBold">
                    {formatMoney(
                      drawdown.approvedAmount || drawdown.requestedAmount,
                      {cents: true}
                    )}{' '}
                    nzd
                  </Text>
                  {canEdit && (
                    <AmountEdit
                      projectUuid={projectUuid}
                      drawdown={drawdown}
                      isRefetching={isRefetching}
                    />
                  )}
                </Space>
                <Text color="grey" weight="medium" size="xs">
                  Requested amount:{' '}
                  {formatMoney(drawdown.requestedAmount, {cents: true})}
                </Text>
              </div>
            </Space>
            <StatusActions
              role={role}
              permissions={permissions}
              projectUuid={projectUuid}
              drawdown={drawdown}
              isRefetching={isRefetching}
            />
          </>
        ) : (
          <div className={styles.wrapper}>
            <Loader />
          </div>
        )}
      </Block>
    </div>
  )
}

const BorrowerDocuments = ({documents}: BorrowerDocumentsProps) => {
  const [isOpen, setOpen] = useState(false)

  const handleToggle = () => setOpen((state) => !state)

  return (
    <>
      {isOpen && (
        <Box fadeIn as="ul" mb={12}>
          {documents.map((document) => (
            <li key={document.uuid}>
              <File
                uuid={document.uuid}
                name={document.name}
                size={document.size}
                url={document.url}
              />
            </li>
          ))}
        </Box>
      )}
      <Button variant="secondary" onClick={handleToggle}>
        {isOpen ? 'hide' : 'open'} documents
      </Button>
    </>
  )
}

const DocumentAttach = ({projectUuid, drawdownUuid}: DrawdownProps) => {
  const {
    mutate: attachDrawdownDocument,
    isMutating: isAttachDrawdownDocumentMutating,
  } = useAttachDrawdownDocument(projectUuid, drawdownUuid)
  const {mutate: uploadFile, isMutating: isUploadFileMutating} = useUploadFile({
    key: drawdownUuid,
    attachFile: (fileUuid) => attachDrawdownDocument(fileUuid),
  })

  const hanldeUpload = ({file}: any) => uploadFile(file)

  const loading = isUploadFileMutating || isAttachDrawdownDocumentMutating

  return (
    <Box mt={12}>
      <Button variant="secondary" loading={loading} onClick={hanldeUpload}>
        add document
      </Button>
    </Box>
  )
}

const ForecastEdit = ({
  projectUuid,
  drawdown,
  forecastDrawdowns,
  isRefetching,
  isForecastRefetching,
}: ForecastEditProps) => {
  const {mutate: updateDrawdown, isMutating} = useUpdateDrawdown(
    projectUuid,
    drawdown.uuid
  )

  const freeForecastDrawdowns = forecastDrawdowns
    .map((item, index) => ({...item, order: index}))  // here we assume the initial drawdown is the first element of the array, and the order of initial drawdown is '0'
    .filter((item) => !item.drawdown)
    .filter((item) => !item.initialDrawdown)

  // console.log(forecastDrawdowns)
  // console.log(freeForecastDrawdowns)

  const menuItems = [
    ...freeForecastDrawdowns.map((item) => ({
      key: item.uuid,
      label: (
        <div className={styles.forecastItem}>
          <Text weight="medium">Estimated drawdown {item.order}</Text>
          <Text weight="bold" color="turquoizeBright">
            {formatMoney(item.value.amount, {cents: true})}
          </Text>
        </div>
      ),
    })),
    {key: 'null', label: <Text weight="medium">without reference</Text>},
  ]

  return (
    <Dropdown
      menu={{
        items: menuItems,
        onClick: ({key}) =>
          updateDrawdown({forecastItem: key === 'null' ? null : key}),
      }}
      disabled={isRefetching}
      placement="bottom"
    >
      <IconButton
        className={styles.forecastButton}
        loading={isForecastRefetching || isMutating}
        icon={<DropdownIcon second />}
      />
    </Dropdown>
  )
}

const AmountEdit = ({projectUuid, drawdown, isRefetching}: ActionProps) => {
  const [isOpen, handleOpen, handleClose] = useOpen()

  return (
    <>
      <IconButton
        icon={<PenEditSecondIcon />}
        disabled={isRefetching}
        onClick={handleOpen}
      />
      <Modal
        width={486}
        open={isOpen}
        title="Drawdown amount"
        onCancel={handleClose}
      >
        <DrawdownAmountForm
          projectUuid={projectUuid}
          drawdown={drawdown}
          onClose={handleClose}
        />
      </Modal>
    </>
  )
}

// 权限没有统一梳理出来，暂时按照角色来判断按钮的展示
// PERMISSIONSENUM.LM_EDIT_DRAWDOWN PERMISSIONSENUM.FC_EDIT_DRAWDOWN)
const StatusActions = ({permissions, role, ...props}: StatusActionsProps) => {
  const {status} = props.drawdown

  switch (status) {
    case DRAWDOWNSTATUS.DOCUMENT_CHECK:  //---------- 1.LM Review
      return (role.manager || role.coordinator) ? <DocumentCheckAction {...props} /> : null;
    
    case DRAWDOWNSTATUS.FINAL_APPROVAL:  //---------- 2.FC Review
      if(role.controller) { 
        return <FinalApprovalActions {...props} />
      }
      if(role.manager || role.coordinator) {
        return <RecallAction {...props} />
      }
      return null;
    
    case DRAWDOWNSTATUS.CONFIRMED:     //----------- 3.在Drawdown confirm(完成全部审核，未对账)时
      return role.controller ? <RecallAction {...props} role={role} /> : null;
    
    case DRAWDOWNSTATUS.APPROVED:      //----------- 4.Pending payment
      return role.user ? <ConfirmAction {...props} /> : null
    default:
      return null;
  }
}

const DocumentCheckAction = ({
  projectUuid,
  drawdown,
  isRefetching,
}: ActionProps) => {
  const {mutate: acceptDocuments, isMutating: isAcceptDocumentsMutating} =
    useAcceptDrawdownDocuments(projectUuid, drawdown.uuid)
  const {mutate: updateDrawdown, isMutating: isUpdateDrawdownMutating} =
    useUpdateDrawdown(projectUuid, drawdown.uuid, acceptDocuments)
  const {mutate: decline, isMutating: isDeclineMutating} = useDeclineDrawdown(
    projectUuid,
    drawdown.uuid
  )

  const handleDecline = () => decline()

  const handleDocumentsAccept = () => {
    drawdown.approvedAmount
      ? acceptDocuments()
      : updateDrawdown({approvedAmount: drawdown.requestedAmount})
  }

  const isLoading =
    isAcceptDocumentsMutating ||
    (!drawdown.approvedAmount && isUpdateDrawdownMutating)

  const disabled = useMemo(() => {
    return (!isLoading && isRefetching) || isDeclineMutating
  }, [isLoading, isRefetching, isDeclineMutating])

  return (
    <Box mt={24}>
      <Button
        block
        loading={isLoading}
        disabled={disabled}
        onClick={handleDocumentsAccept}
      >
        Accept documents
      </Button>
      <Box align="center">
        <Text color="grey" size="xs" weight="medium" mt={24} mb={12}>
          You can decline the drawdown request by clicking the button below.
        </Text>
        <Confirm
          title="Are you sure you want to decline the request?"
          okText="decline"
          onConfirm={handleDecline}
        >
          <Button
            variant="danger"
            loading={isDeclineMutating}
            disabled={disabled}
          >
            decline request
          </Button>
        </Confirm>
      </Box>
    </Box>
  )
}

const FinalApprovalActions = ({
  projectUuid,
  drawdown,
  isRefetching,
}: ActionProps) => {
  const {mutate: approve, isMutating: isApproveMutating} = useApproveDrawdown(
    projectUuid,
    drawdown.uuid
  )
  const {mutate: decline, isMutating: isDeclineMutating} = useDeclineDrawdown(
    projectUuid,
    drawdown.uuid
  )

  const handleApprove = () => approve()

  const handleDecline = () => decline()

  return (
    <Box mt={24}>
      {/* 只有FC角色才能在FC review时进行审核 */}
      <Button
        block
        loading={isApproveMutating}
        disabled={isRefetching || isDeclineMutating}
        onClick={handleApprove}
      >
        Approve
      </Button>
      {/* 
        不抽取出decline按钮为一个单独的组件原因在于：decline按钮和上面的按钮（approve 或 accept document）在disbaled上互相影响
        抽取出来组件后结构复杂度上升，逻辑上也不便于理解，所以抽取此组件，加一段重复代码相对更合理一下
      */}
      <Box align="center">
        <Text color="grey" size="xs" weight="medium" mt={24} mb={12}>
          You can decline the drawdown request by clicking the button below.
        </Text>
        <Confirm
          title="Are you sure you want to decline the request?"
          okText="decline"
          onConfirm={handleDecline}
        >
          <Button
            variant="danger"
            loading={isDeclineMutating}
            disabled={isRefetching || isApproveMutating}
          >
            decline request
          </Button>
        </Confirm>
      </Box>
      <PushbackModal projectUuid={projectUuid} drawdownUuid={drawdown.uuid} />
    </Box>
  )
}

const RecallAction = ({projectUuid, drawdown, role}: ActionProps & { role?: ProfileRole }) => {
  const {mutate: recall, isMutating: isRecallMutating} = useRecallDrawdown(
    projectUuid,
    drawdown.uuid,
    role?.controller
  );
  const handleRecall = () => recall();

  return (
    <Box align="center" mt={24} mb={12}>
      <Confirm
        title="Are you sure you want to recall the request?"
        okText="recall"
        onConfirm={handleRecall}
      >
        <Button
          block
          loading={isRecallMutating}
        >
          Recall
        </Button>
      </Confirm>
    </Box>
  )
}

const ConfirmAction = ({projectUuid, drawdown, isRefetching}: ActionProps) => {
  const [isOpen, handleOpen, handleClose] = useOpen()

  return (
    <Box mt={24}>
      <Button block disabled={isRefetching} onClick={handleOpen}>
        Confirm with TXT code
      </Button>
      <Modal width={486} open={isOpen} onCancel={handleClose}>
        <DrawdownConfirm
          projectUuid={projectUuid}
          drawdown={drawdown}
          onClose={handleClose}
        />
      </Modal>
    </Box>
  )
}
